
/*
 * Filename: client-carousel.js
 * By: J. Nguyen
 * Date: 2/9/23
 *
 * Description: 
 * This will contain the logic for the client carousel.
 *
 */


export default function ClientCarousel(element, optionOverwrites) {

  const elem = element;                       // remember the DOM element that this object is bound to
  let api = {};                               // Object to contain the publicly accessible functions

  // default options
  const defaults = {
    debounceTiming: 250,
    debugMode: false,
    largeScreenBreakPt: 992
  };

  // overwrite options based on optionOverwrites
  const options = Object.assign(defaults, optionOverwrites);
  
  // DOM NODES
  const logoNodes = elem.getElementsByClassName('client-carousel__client');
  const quoteContainerNode = elem.getElementsByClassName('client-carousel__quotes')[0];
  const quoteNodes = elem.getElementsByClassName('client-carousel__client-quote');

  const prevBtnNode = elem.getElementsByClassName('client-carousel__prev-btn')[0];
  const nextBtnNode = elem.getElementsByClassName('client-carousel__next-btn')[0];

  // DATA VARIABLES - private variables
  const panelInitClass = 'panel--init';
  const activeLogoClass = 'client--active';
  const activeQuoteClass = 'quote--active';
  const disabledBtnClass = 'btn--disabled';

  let activeClientIndex = 0;                         // tracker for which client index value is active
  let isPrevActive = false;                          // tracks if prev btn should be active or disabled
  let isNextActive = false;                          // tracks if next btn should be active or disabled
  let totalClients = logoNodes.length;               // total count of clients in consideration
  let quoteContainerHeight = 0;                      // dynamically set the container height
  let resizeTimer = null;                            // handle for throttling down resize event

  init();

  // initilize the module
  function init() {
    debug('insider ClientCarousel init()');

    window.onload = determineQuoteHeight;

    // Resize handler
    window.addEventListener('resize', resizeHandler);

    // Client logo event handling
    for (let x=0; x < logoNodes.length; x++) {
      logoNodes[x].addEventListener("click", processClientClick);
    }

    // prev/next button event handling
    prevBtnNode.addEventListener("click", processPrevClick);
    nextBtnNode.addEventListener("click", processNextClick);

  }


  // HELPER FUNCTIONS
  //===============================================
  
  // Basic debug function
  function debug(msg) { 
    if (defaults.debugMode) {
      console.log(msg);
    }
  }

  // PRIVATE FUNCTIONS
  //===============================================
  
  // resize handler - debounced by the debounceTiming value
  function resizeHandler(event) {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      determineQuoteHeight();       
    }, options.debounceTiming);
  }


  // clear active client logos and quotes
  function clearClientData() {
    for (let x=0; x < logoNodes.length; x++){
      logoNodes[x].classList.remove(activeLogoClass);
      quoteNodes[x].classList.remove(activeQuoteClass);
    }
  }

  // activate a client based on index value
  function activateClient() {
    logoNodes[activeClientIndex].classList.add(activeLogoClass);
    quoteNodes[activeClientIndex].classList.add(activeQuoteClass);
  }

  // update prev/next active or disabled states based on current activeIndex
  function updatePrevNextTracking() {
    // disable prev
    if (activeClientIndex == 0) {
      prevBtnNode.classList.add(disabledBtnClass);
    } else {
      prevBtnNode.classList.remove(disabledBtnClass);
    }

    // disable next
    if (activeClientIndex == (totalClients - 1)) {
      nextBtnNode.classList.add(disabledBtnClass);
    } else {
      nextBtnNode.classList.remove(disabledBtnClass);
    }
  }


  
  // PUBLIC FUNCTIONS
  //===============================================
  
  // This function will calculate the height of the quote nodes and store the 
  // highest value to update 
  function determineQuoteHeight() {
    debug("inside determineQuoteHeight()");  

    let tallestHeight = 0;

    for (let x=0; x < quoteNodes.length; x++) {
      if ( quoteNodes[x].offsetHeight > tallestHeight) {
        tallestHeight = quoteNodes[x].offsetHeight;
        debug('quote element height is: ' + quoteNodes[x].offsetHeight);
      }
    }

    debug('tallest height is: ' + tallestHeight);
    quoteContainerNode.style.height = tallestHeight + 'px';

    if ( !elem.classList.contains(panelInitClass) ) {
      elem.classList.add(panelInitClass);
    }

  }


  // This function will only apply on large screen sizes.  It will
  // activate a clicked client logo and trigger activation of the 
  // corresponding quote text.
  function processClientClick(event) {
    let screenWidth = window.innerWidth;
    let logoTarget = event.target;
    const nearestClient = logoTarget.closest('.client-carousel__client');

    // check if in large screen use case
    if ( screenWidth > options.largeScreenBreakPt ) {

      // check if new client was selected
      if ( nearestClient != logoNodes[activeClientIndex] ) {

        clearClientData();

        // Update active logo and active quote
        activeClientIndex = [].indexOf.call(logoNodes, nearestClient);
        activateClient();

        // Update prev/next tracking
        updatePrevNextTracking();
      } 

    }
  }


  // handle prev btn click
  function processPrevClick(event) {
    if (activeClientIndex != 0) {
      clearClientData();
      activeClientIndex--;
      activateClient();
      updatePrevNextTracking();
    }
  }

  // handle next btn click
  function processNextClick(event) {
    if (activeClientIndex != (totalClients - 1)) {
      clearClientData();
      activeClientIndex++;
      activateClient();
      updatePrevNextTracking();
    }
  }
  
  
  // EXPOSE PUBLIC FUNCTIONS VIA THE API
  // Map all the functions we want to expose for public access
  //===============================================================
  api.determineQuoteHeight = determineQuoteHeight;
  api.processClientClick = processClientClick;
  api.processPrevClick = processPrevClick;
  api.processNextClick = processNextClick;
  return api;

}
